import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { useCart } from "@/context/CartContext";
import { api, resolvePhotoUrl } from "@/lib/api";
import { Minus, Plus, Trash2, ChevronLeft, Ticket, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function Checkout() {
    const navigate = useNavigate();
    const { items, updateQty, removeItem, subtotal, clear } = useCart();
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [senderDetail, setSenderDetail] = useState("");
    const [address, setAddress] = useState("");
    const [notes, setNotes] = useState("");
    const lastDepartment = sessionStorage.getItem("last_department");
    const addMorePath = lastDepartment ? `/shop/${encodeURIComponent(lastDepartment)}` : "/";
    const [submitting, setSubmitting] = useState(false);
    const [promos, setPromos] = useState([]);
    const [promoCode, setPromoCode] = useState("");

    useEffect(() => {
        api.get("/promos/public")
            .then((r) => setPromos(Array.isArray(r.data) ? r.data : []))
            .catch(() => setPromos([]));
    }, []);

    const selectedPromo = useMemo(
        () => promos.find((p) => p.code === promoCode) || null,
        [promos, promoCode]
    );
    const discount = useMemo(() => {
        if (!selectedPromo) return 0;
        return Math.round(subtotal * (Number(selectedPromo.percent) || 0)) / 100;
    }, [selectedPromo, subtotal]);
    const total = Math.max(0, Math.round((subtotal - discount) * 100) / 100);

    const submit = async () => {
        if (items.length === 0) return;
        if (!name.trim() || !phone.trim() || !address.trim()) {
            toast.error("Name, Contact No and Delivery Address are required");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/orders", {
                customer_name: name,
                customer_phone: phone,
                sender_detail: senderDetail,
                customer_address: address,
                notes,
                promo_code: promoCode || null,
                items: items.map((i) => ({
                    product_id: i.product_id,
                    quantity: i.quantity,
                })),
            });
            clear();
            navigate(`/order/${data.resource_id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not place order");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MobileShell showCart={false}>
            <button
                data-testid="checkout-back"
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-sm text-muted-foreground py-2"
            >
                <ChevronLeft className="h-4 w-4" />
                Back
            </button>
            <h1 className="font-serif text-4xl md:text-5xl leading-none mt-2 md:mt-4">
                Your bag
            </h1>

            {items.length === 0 ? (
                <div
                    data-testid="empty-cart-state"
                    className="mt-16 text-center space-y-4"
                >
                    <p className="text-muted-foreground">Your bag is empty.</p>
                    <Link
                        to="/"
                        data-testid="start-shopping"
                        className="inline-block rounded-full bg-primary text-primary-foreground px-6 py-3 hover:bg-primary-hover transition-colors"
                    >
                        Start shopping
                    </Link>
                </div>
            ) : (
                <div className="md:grid md:grid-cols-[1fr_400px] md:gap-10 md:items-start md:mt-10">
                    <div>
                        <ul className="mt-6 md:mt-0 space-y-3" data-testid="cart-list">
                        {items.map((i) => (
                            <li
                                key={i.product_id}
                                data-testid={`cart-item-${i.product_id}`}
                                className="flex gap-3 bg-surface/40 border border-border/60 rounded-2xl p-4"
                            >
                                <div className="w-16 h-16 rounded-xl overflow-hidden bg-border/20 shrink-0">
                                    {i.photo_url && (
                                        <img
                                            src={resolvePhotoUrl(i.photo_url)}
                                            alt={i.name}
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-serif text-base truncate">
                                        {i.product_id ? (
                                            <Link
                                                to={`/product/${i.product_id}`}
                                                className="underline decoration-border/70 underline-offset-2 hover:text-primary transition-colors"
                                            >
                                                {i.name}
                                            </Link>
                                        ) : (
                                            i.name
                                        )}
                                    </p>
                                    <p className="text-primary font-semibold text-sm">
                                        ₹{i.price.toFixed(2)}
                                    </p>
                                    <div className="mt-1 flex items-center gap-2">
                                        <button
                                            data-testid={`cart-decrement-${i.product_id}`}
                                            onClick={() => updateQty(i.product_id, i.quantity - 1)}
                                            className="p-1 rounded-full border border-border/60 hover:border-foreground"
                                        >
                                            <Minus className="h-3 w-3" />
                                        </button>
                                        <span className="text-sm w-6 text-center">{i.quantity}</span>
                                        <button
                                            data-testid={`cart-increment-${i.product_id}`}
                                            onClick={() => updateQty(i.product_id, i.quantity + 1)}
                                            className="p-1 rounded-full border border-border/60 hover:border-foreground"
                                        >
                                            <Plus className="h-3 w-3" />
                                        </button>
                                        <button
                                            data-testid={`cart-remove-${i.product_id}`}
                                            onClick={() => removeItem(i.product_id)}
                                            className="ml-auto text-muted-foreground hover:text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                        <div className="mt-3">
                            <Link
                                to={addMorePath}
                                data-testid="add-more-items-btn"
                                className="inline-flex items-center gap-1.5 rounded-full border border-primary text-primary font-medium px-4 py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors text-xs"
                            >
                                <Plus className="h-3 w-3" />
                                Add more items
                            </Link>
                        </div>
                    </div>

                    <aside className="md:sticky md:top-24 md:bg-card md:border md:border-border/60 md:rounded-3xl md:p-6">
                    <div className="mt-6 md:mt-0 border-t md:border-t-0 border-border/40 pt-4 md:pt-0 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm uppercase tracking-[0.2em] text-muted-foreground">
                                Subtotal
                            </span>
                            <span
                                data-testid="cart-subtotal"
                                className="font-serif text-xl text-foreground"
                            >
                                ₹{subtotal.toFixed(2)}
                            </span>
                        </div>

                        {promos.length > 0 && (
                            <div className="pt-3" data-testid="promo-selector-block">
                                <Label className="text-xs uppercase tracking-[0.2em] flex items-center gap-1">
                                    <Ticket className="h-3 w-3" /> Promo code
                                </Label>
                                <div className="mt-1 flex items-center gap-2">
                                    <Select
                                        value={promoCode || "none"}
                                        onValueChange={(v) => setPromoCode(v === "none" ? "" : v)}
                                    >
                                        <SelectTrigger
                                            data-testid="promo-select"
                                            className="rounded-2xl h-11 flex-1"
                                        >
                                            <SelectValue placeholder="Choose a promo" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No promo</SelectItem>
                                            {promos.map((p) => (
                                                <SelectItem key={p.id} value={p.code} data-testid={`promo-option-${p.code}`}>
                                                    {p.code} — {p.percent}% off
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {promoCode && (
                                        <button
                                            type="button"
                                            data-testid="promo-clear"
                                            onClick={() => setPromoCode("")}
                                            className="h-11 w-11 rounded-2xl border border-border/60 flex items-center justify-center hover:border-foreground"
                                            aria-label="Clear promo"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                                {selectedPromo && (
                                    <p
                                        data-testid="promo-applied-note"
                                        className="mt-2 text-xs text-moss"
                                    >
                                        {selectedPromo.code} applied — you save ₹{discount.toFixed(2)}
                                    </p>
                                )}
                            </div>
                        )}

                        {selectedPromo && (
                            <div className="flex items-center justify-between pt-2" data-testid="cart-discount-row">
                                <span className="text-sm uppercase tracking-[0.2em] text-muted-foreground">
                                    Discount ({selectedPromo.percent}%)
                                </span>
                                <span className="font-serif text-lg text-moss">
                                    -₹{discount.toFixed(2)}
                                </span>
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-3 border-t border-border/40">
                            <span className="text-sm uppercase tracking-[0.2em] text-muted-foreground">
                                Total
                            </span>
                            <span
                                data-testid="cart-total"
                                className="font-serif text-2xl font-semibold text-primary"
                            >
                                ₹{total.toFixed(2)}
                            </span>
                        </div>
                    </div>

                    <section className="mt-8 md:mt-6 space-y-5">
                        <h2 className="font-serif text-2xl font-semibold">Delivery details</h2>
                        <div>
                            <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground">
                                Full Name <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="name"
                                data-testid="customer-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Full name for delivery"
                                className="mt-2 h-13 rounded-2xl text-base border-2 focus:border-primary"
                            />
                        </div>
                        <div>
                            <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground">
                                Contact No <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="phone"
                                data-testid="customer-phone-input"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="Contact number for delivery"
                                className="mt-2 h-13 rounded-2xl text-base border-2 focus:border-primary"
                            />
                        </div>
                        <div>
                            <Label htmlFor="address" className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground">
                                Delivery Address <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                id="address"
                                data-testid="customer-address-input"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                rows={3}
                                placeholder="Full delivery address"
                                className="mt-2 rounded-2xl text-base border-2 focus:border-primary"
                            />
                        </div>
                        <div>
                            <Label htmlFor="recipient-phone" className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground">
                              Sender Name <span className="text-muted-foreground font-normal normal-case tracking-normal">(optional)</span>
                            </Label>
                            <Input
                                id="recipient-phone"
                                data-testid="recipient-phone-input"
                                value={senderDetail}
                                onChange={(e) => setSenderDetail(e.target.value)}
                                placeholder="Sender's name"
                                className="mt-2 h-13 rounded-2xl text-base border-2 focus:border-primary"
                            />
                        </div>
                        <div>
                            <Label htmlFor="notes" className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground">
                                Notes <span className="text-muted-foreground font-normal normal-case tracking-normal">(optional)</span>
                            </Label>
                            <Textarea
                                id="notes"
                                data-testid="customer-notes-input"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                placeholder="Any special instructions"
                                className="mt-2 rounded-2xl text-base border-2 focus:border-primary"
                            />
                        </div>
                        <Button
                            data-testid="place-order-btn"
                            disabled={submitting}
                            onClick={submit}
                            className="w-full h-14 rounded-full bg-primary hover:bg-primary-hover text-primary-foreground text-base"
                        >
                            {submitting ? "Placing…" : `Place order · ₹${total.toFixed(2)}`}
                        </Button>
                    </section>
                    </aside>
                </div>
            )}
        </MobileShell>
    );
}
